// components/KioskLayout.jsx
import React, { useState, useEffect } from 'react';
import { useIdleDetection } from '../hooks/useIdleDetection';
import CampaignPlayer from './CampaignPlayer';
import KioskSetup from './KioskSetup';
import { getKioskConfig } from '../utils/kioskConfig';

/**
 * Kiosk Layout Wrapper
 * Manages idle detection and campaign display
 * 
 * Props:
 * - children: The kiosk interface content
 * - idleTimeoutMs: Time before showing campaigns (default: 30000 = 30s)
 * - campaignHeightPercent: Campaign area height when idle (default: 75)
 */
export default function KioskLayout({ 
  children, 
  idleTimeoutMs = 30000,
  campaignHeightPercent = 75 
}) {
  const [config, setConfig] = useState(getKioskConfig());
  const [isMascotOnlyMode, setIsMascotOnlyMode] = useState(false);
  const { isIdle, resetTimer } = useIdleDetection(idleTimeoutMs);

  // Check if we're in kiosk mode
  const urlParams = new URLSearchParams(window.location.search);
  const isKioskMode = urlParams.get('kiosk') === '1';

  useEffect(() => {
    // Refresh config when component mounts or screen_id param changes
    setConfig(getKioskConfig());
  }, []);

  // Not in kiosk mode - render normally
  if (!isKioskMode) {
    return <>{children}</>;
  }

  // Kiosk mode but not configured - show setup screen
  if (!config.isConfigured) {
    return (
      <KioskSetup 
        onConfigured={() => {
          setConfig(getKioskConfig());
        }}
      />
    );
  }

  // Kiosk mode and configured - show layout with campaigns
  const kioskHeightPercent = 100 - campaignHeightPercent;

  return (
    <div 
      onClick={resetTimer}
      onTouchStart={resetTimer}
      style={styles.container}
    >
      {/* Campaign Player - absolute overlay when idle */}
      <div style={{
        ...styles.campaignArea,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: isIdle ? '75%' : '0%',
        opacity: isIdle ? 1 : 0,
        zIndex: isIdle ? 100 : -1,
        pointerEvents: 'none'
      }}>
        {isIdle && (
          <CampaignPlayer 
            screenId={config.screenId} 
            apiBase={config.apiBase}
            onPlaybackStateChange={(state) => setIsMascotOnlyMode(state.mascotOnly)}
          />
        )}
      </div>

      {/* Kiosk Interface - always full height */}
      <div style={{
        ...styles.kioskArea,
        height: '100%',
        background: isIdle ? 'transparent' : '#fff'
      }}>
        {/* Overlay when idle (hidden for mascot-only campaigns) */}
        {isIdle && !isMascotOnlyMode && (
          <div style={styles.overlay} onClick={resetTimer}>
            <div style={styles.overlayContent}>
              <span style={styles.overlayIcon}>👆</span>
              <h2 style={styles.overlayText}>Touch to Start Shopping</h2>
              <p style={styles.overlaySubtext}>Tap anywhere to begin</p>
            </div>
          </div>
        )}
        
        {/* Main kiosk content */}
        <div style={{
          ...styles.contentWrapper,
          pointerEvents: isIdle ? 'none' : 'auto',
          opacity: 1,  // Always visible, campaign overlay covers it when needed
          visibility: 'visible'
        }}>
          {children}
        </div>
      </div>

      {/* Debug info (remove in production) */}
      {import.meta.env.DEV && (
        <div style={styles.debugInfo}>
          <small>
            Screen: {config.screenId?.substring(0, 8)}... | 
            Idle: {isIdle ? 'Yes' : 'No'} | 
            Timeout: {idleTimeoutMs / 1000}s
          </small>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#000',
    position: 'relative'
  },
  campaignArea: {
    width: '100%',
    transition: 'height 0.5s ease-in-out, opacity 0.5s ease-in-out',
    overflow: 'hidden',
    background: 'transparent',
    pointerEvents: 'none'  // Clicks pass through to products below
  },
  kioskArea: {
    width: '100%',
    transition: 'height 0.5s ease-in-out',
    overflow: 'hidden',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    flexShrink: 0
  },
  overlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '25%',
    background: 'rgba(0, 0, 0, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    cursor: 'pointer',
    animation: 'pulse 2s ease-in-out infinite',
    pointerEvents: 'auto'
  },
  overlayContent: {
    textAlign: 'center',
    color: '#fff',
    padding: '40px'
  },
  overlayIcon: {
    fontSize: '80px',
    display: 'block',
    marginBottom: '20px',
    animation: 'bounce 1s ease-in-out infinite'
  },
  overlayText: {
    fontSize: '48px',
    fontWeight: '700',
    margin: '0 0 16px',
    textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
  },
  overlaySubtext: {
    fontSize: '24px',
    color: 'rgba(255, 255, 255, 0.8)',
    margin: 0
  },
  contentWrapper: {
    flex: 1,
    overflow: 'auto',
    transition: 'opacity 0.3s ease-in-out'
  },
  debugInfo: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    color: '#0f0',
    padding: '4px 8px',
    fontSize: '11px',
    fontFamily: 'monospace',
    zIndex: 9999,
    textAlign: 'center'
  }
};

// Add keyframe animations
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.95; }
    }
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-10px); }
    }
  `;
  document.head.appendChild(style);
}
